// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Coverage, Organization, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { fetchPrimaryCoverage, fetchSecondaryCoverage, resolvePayorOrganization } from '../utils/patient-coverage';

export interface UsePatientPrimaryCoverageResult {
  coverage: Coverage | undefined;
  secondaryCoverage: Coverage | undefined;
  payor: Organization | undefined;
  loading: boolean;
  error: Error | null;
  setCoverage: (coverage: Coverage, payor?: Organization, secondaryCoverage?: Coverage) => void;
  refresh: () => void;
}

/**
 * Loads the patient's primary Coverage plus the resolved payor Organization.
 * Returns undefined for both until loaded. `setCoverage` lets callers update the
 * cache optimistically after editing.
 */
export function usePatientPrimaryCoverage(patient: Patient | undefined): UsePatientPrimaryCoverageResult {
  const medplum = useMedplum();
  const [coverage, setCoverageState] = useState<Coverage | undefined>();
  const [payor, setPayorState] = useState<Organization | undefined>();
  const [secondaryCoverage, setSecondaryCoverage] = useState<Coverage | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAll = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const cov = await fetchPrimaryCoverage(medplum, patient.id);
      setCoverageState(cov);
      setSecondaryCoverage(await fetchSecondaryCoverage(medplum, patient.id, cov?.id));
      if (cov) {
        const org = await resolvePayorOrganization(medplum, cov);
        setPayorState(org);
      } else {
        setPayorState(undefined);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load coverage'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    fetchAll().catch(console.error);
  }, [fetchAll]);

  const setCoverage = useCallback((next: Coverage, nextPayor?: Organization, nextSecondary?: Coverage) => {
    setCoverageState(next);
    setSecondaryCoverage(nextSecondary);
    if (nextPayor !== undefined) {
      setPayorState(nextPayor);
    }
  }, []);

  return { coverage, secondaryCoverage, payor, loading, error, setCoverage, refresh: fetchAll };
}
